import * as React from 'react';
import { DataGrid, GridColDef, GridToolbarContainer } from '@mui/x-data-grid';
import {
  Box,
  Typography,
  CircularProgress,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  IconButton,
  Divider,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { BarberServices } from '../../types/barber';
import { useContext, useMemo, useState } from 'react';
import { roleContext } from '../../context/RoleContext';
import AddServices from '../components/AddServices';
import {
  barberUpdateService,
  shopBarberUpdateService,
  shopOwnerUpdateService,
} from '../../middleware/registration_service/updateRequest';
import { UserRole } from '../../types/role';
import {
  barberDeleteServices,
  shopBarberDeleteServices,
  shopOwnerDeleteServices,
} from '../../middleware/registration_service/deleteRequest';
import { useAuth0 } from '@auth0/auth0-react';
import { useProfileData } from '../../context/ProfileDataContext';

export default function ServicesListings() {
  const {
    barberId,
    shopId,
    barber,
    shopBarber,
    shop,
    partner,
    loading,
    setBarber,
    setShopBarber,
    setShop,
  } = useProfileData();
  const { getAccessTokenSilently } = useAuth0();
  const shopOwnerId = partner?.id ?? null;

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editRow, setEditRow] = useState<BarberServices | null>(null);
  const [editName, setEditName] = useState('');
  const [editPrice, setEditPrice] = useState('');
  const [editDuration, setEditDuration] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [saving, setSaving] = useState(false);

  const role = useContext(roleContext);

  const services: BarberServices[] = useMemo(() => {
    if (role === UserRole.BARBER) {
      return barber?.barberServices ?? [];
    }
    if (role === UserRole.SHOP_BARBER) {
      return shopBarber?.barberShopServices ?? [];
    }
    if (role === UserRole.SHOP_OWNER) {
      const allServices: BarberServices[] = [];
      const shopBarbers = shop?.shopBarbers ?? [];
      for (const sb of shopBarbers) {
        for (const svc of sb?.barberShopServices || []) {
          allServices.push(svc);
        }
      }
      return allServices;
    }
    return [];
  }, [
    role,
    barber?.barberServices,
    shopBarber?.barberShopServices,
    shop?.shopBarbers,
  ]);

  const openEditDialog = (row: BarberServices) => {
    setEditRow(row);
    setEditName(row.name ?? '');
    setEditPrice(String(row.price ?? ''));
    setEditDuration(String(row.duration ?? ''));
    setConfirmingDelete(false);
    setEditDialogOpen(true);
  };

  const closeEditDialog = () => {
    setEditDialogOpen(false);
    setEditRow(null);
    setConfirmingDelete(false);
  };

  const handleUpdate = async () => {
    if (!editRow || !editRow.id) return;
    const serviceId = editRow.id;
    setSaving(true);
    const parsedPrice = Number(editPrice);
    const parsedDuration = Number(editDuration);

    if (
      !editName.trim() ||
      !Number.isFinite(parsedDuration) ||
      !Number.isFinite(parsedPrice)
    ) {
      console.error(
        'Validation error: Please provide valid name, price, and duration.',
        { name: editName, editPrice, editDuration }
      );
      setSaving(false);
      return;
    }

    const updatedRow: BarberServices = {
      ...editRow,
      name: editName.trim(),
      price: parsedPrice,
      duration: parsedDuration,
    };
    try {
      const token = await getAccessTokenSilently();
      if (role === UserRole.BARBER) {
        await barberUpdateService(
          editName,
          parsedPrice,
          parsedDuration,
          serviceId,
          barberId,
          token
        );
        setBarber((prev) =>
          prev
            ? {
                ...prev,
                barberServices: (prev.barberServices ?? []).map((s) =>
                  s.id === editRow.id ? updatedRow : s
                ),
              }
            : prev
        );
      } else if (role === UserRole.SHOP_BARBER) {
        await shopBarberUpdateService(
          editName,
          parsedPrice,
          parsedDuration,
          serviceId,
          barberId,
          token
        );
        setShopBarber((prev) =>
          prev
            ? {
                ...prev,
                barberShopServices: (prev.barberShopServices ?? []).map((s) =>
                  s.id === editRow.id ? updatedRow : s
                ),
              }
            : prev
        );
      } else if (role === UserRole.SHOP_OWNER && shopOwnerId) {
        await shopOwnerUpdateService(
          editName,
          parsedPrice,
          parsedDuration,
          serviceId,
          shopOwnerId,
          shopId,
          token
        );
        setShop((prev) =>
          prev
            ? {
                ...prev,
                shopBarbers: (prev.shopBarbers ?? []).map((sb) => ({
                  ...sb,
                  barberShopServices: (sb.barberShopServices ?? []).map((s) =>
                    s.id === editRow.id ? updatedRow : s
                  ),
                })),
              }
            : prev
        );
      }
      closeEditDialog();
    } catch (error) {
      console.error('Error updating service:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editRow || !editRow.id) return;
    const serviceId = editRow.id;
    setSaving(true);
    try {
      const token = await getAccessTokenSilently();
      switch (role) {
        case UserRole.BARBER:
          await barberDeleteServices(serviceId, barberId, token);
          setBarber((prev) =>
            prev
              ? {
                  ...prev,
                  barberServices: (prev.barberServices ?? []).filter(
                    (s) => s.id !== serviceId
                  ),
                }
              : prev
          );
          break;
        case UserRole.SHOP_BARBER:
          await shopBarberDeleteServices(serviceId, shopId, barberId, token);
          setShopBarber((prev) =>
            prev
              ? {
                  ...prev,
                  barberShopServices: (prev.barberShopServices ?? []).filter(
                    (s) => s.id !== serviceId
                  ),
                }
              : prev
          );
          break;
        case UserRole.SHOP_OWNER:
          if (!shopOwnerId) return;
          await shopOwnerDeleteServices(
            serviceId,
            shopOwnerId,
            shopId,
            barberId,
            token
          );
          setShop((prev) =>
            prev
              ? {
                  ...prev,
                  shopBarbers: (prev.shopBarbers ?? []).map((sb) => ({
                    ...sb,
                    barberShopServices: (sb.barberShopServices ?? []).filter(
                      (s) => s.id !== serviceId
                    ),
                  })),
                }
              : prev
          );
          break;
        default:
          return;
      }
      closeEditDialog();
    } catch (error) {
      console.error('Error deleting service:', error);
    } finally {
      setSaving(false);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Services',
      width: 250,
      headerClassName: 'custom-header',
      cellClassName: 'custom-cell',
    },
    { field: 'price', headerName: 'Price', width: 180 },
    { field: 'duration', headerName: 'Duration', width: 180 },
    {
      field: 'actions',
      headerName: '',
      sortable: false,
      width: 60,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={() => openEditDialog(params.row)}
          aria-label="Edit service"
        >
          <EditIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  const CustomToolbar = () => (
    <GridToolbarContainer>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          flexWrap: 'wrap',
          gap: 1,
          px: 1,
          py: 0.5,
        }}
      >
        {role !== UserRole.SHOP_OWNER && <AddServices />}
      </Box>
    </GridToolbarContainer>
  );

  const paginationModel = { page: 0, pageSize: 5 };

  return (
    <Grid container spacing={2}>
      <Box
        sx={{ h: 'auto', width: '100%', maxWidth: 'none', minHeight: 'auto' }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : services.length === 0 ? (
          <Box>
            {/* Header row — keep the title and Add button visible */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                flexWrap: 'wrap',
                gap: 1,
                px: 1.5,
                pt: 2,
                pb: 1,
              }}
            >
              {role !== UserRole.SHOP_OWNER && <AddServices />}
            </Box>

            {/* Empty-state prompt */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 6,
                px: 3,
                mx: 1.5,
                mb: 2,
                borderRadius: '10px',
                border: '2px dashed #e0e0e0',
                backgroundColor: '#fafafa',
                textAlign: 'center',
                gap: 1.5,
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'GFS Didot',
                  fontWeight: 'bold',
                  fontSize: '16px',
                  color: '#555',
                }}
              >
                No services added yet
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'GFS Didot',
                  fontSize: '13px',
                  color: '#999',
                  maxWidth: 320,
                }}
              >
                {role === UserRole.SHOP_OWNER
                  ? 'Services will appear here once your barbers have added them.'
                  : 'Get started by adding your first service — set a name, price, and duration.'}
              </Typography>
              {role !== UserRole.SHOP_OWNER && (
                <Box sx={{ mt: 1 }}>
                  <AddServices />
                </Box>
              )}
            </Box>
          </Box>
        ) : (
          <DataGrid
            rows={services}
            columns={columns}
            initialState={{ pagination: { paginationModel } }}
            slots={{ toolbar: CustomToolbar }}
            pageSizeOptions={[5, 10]}
            disableRowSelectionOnClick
          />
        )}

        {/* Edit / Delete dialog */}
        <Dialog
          open={editDialogOpen}
          onClose={closeEditDialog}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle sx={{ fontFamily: 'GFS Didot', fontWeight: 'bold' }}>
            Edit Service
          </DialogTitle>
          <DialogContent
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              pt: '12px !important',
            }}
          >
            <TextField
              label="Service name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Price"
              value={editPrice}
              onChange={(e) => setEditPrice(e.target.value)}
              fullWidth
              size="small"
              type="number"
            />
            <TextField
              label="Duration"
              value={editDuration}
              onChange={(e) => setEditDuration(e.target.value)}
              fullWidth
              size="small"
              type={'number'}
            />
          </DialogContent>

          {!confirmingDelete ? (
            <DialogActions
              sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}
            >
              <Button
                color="error"
                variant="outlined"
                onClick={() => setConfirmingDelete(true)}
                disabled={saving}
                sx={{ fontFamily: 'GFS Didot', fontWeight: 'bold' }}
              >
                Delete
              </Button>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  onClick={closeEditDialog}
                  disabled={saving}
                  variant="outlined"
                  sx={{
                    fontFamily: 'GFS Didot',
                    fontWeight: 'bold',
                    borderColor: '#333',
                    color: '#333',
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleUpdate}
                  disabled={saving}
                  sx={{
                    bgcolor: '#dd8709',
                    color: '#fff',
                    fontFamily: 'GFS Didot',
                    fontWeight: 'bold',
                    '&:hover': { bgcolor: '#b86e08' },
                    '&.Mui-disabled': { bgcolor: '#e0b96a', color: '#fff' },
                  }}
                >
                  {saving ? 'Saving…' : 'Save'}
                </Button>
              </Box>
            </DialogActions>
          ) : (
            <>
              <Divider />
              <DialogContent sx={{ pt: '12px !important' }}>
                <Typography variant="body2" color="text.secondary">
                  Are you sure you want to delete <strong>{editName}</strong>?
                  This cannot be undone.
                </Typography>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                  onClick={() => setConfirmingDelete(false)}
                  disabled={saving}
                  variant="outlined"
                  sx={{
                    fontFamily: 'GFS Didot',
                    fontWeight: 'bold',
                    borderColor: '#333',
                    color: '#333',
                  }}
                >
                  No, keep it
                </Button>
                <Button
                  color="error"
                  variant="contained"
                  onClick={handleDelete}
                  disabled={saving}
                  sx={{ fontFamily: 'GFS Didot', fontWeight: 'bold' }}
                >
                  {saving ? 'Deleting…' : 'Yes, delete'}
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Box>
    </Grid>
  );
}
